import React, { useMemo, useState, useEffect } from "react"
import style from "./gallery-new.mod.scss"
import FilteredList from "./filtered-list"
import Modal from "./modal"
import Button from "./button"
import Video from "../elements/video"
import { graphql } from "gatsby"
import { GatsbyImage } from "gatsby-plugin-image"
import {
  removeDuplicates,
  configTagsForSelect,
  filterByLocale
} from "@helpers/miscHelpers"

import "../elements/gallery-item"
import { FaAngleRight } from "@react-icons/all-files/fa/FaAngleRight"
import { FaTimes } from "@react-icons/all-files/fa/FaTimes"
import { MdKeyboardArrowLeft } from "@react-icons/all-files/md/MdKeyboardArrowLeft"
import { MdKeyboardArrowRight } from "@react-icons/all-files/md/MdKeyboardArrowRight"

const slugify = s =>
  typeof s === "string"
    ? s
        .toLowerCase()
        .replace(/\s+/g, "-")
        .replace(/[^\w-]/g, "")
    : ""

const transformTagToObject = tag => {
  if (!tag?.name)
    return {
      name: tag?.title || tag,
      slug:
        tag?.slug ||
        (typeof (tag?.title || tag) === "string"
          ? slugify(tag?.title || tag)
          : "")
    }

  return tag
}

const configureTags = arr => {
  if (arr && arr.length > 0) {
    const orderedArr = arr.sort((a, b) => a.name.localeCompare(b.name))
    return removeDuplicates(orderedArr)
  }
  return []
}

const getItemLinkTo = item =>
  item?.link?.link?.path || item?.link?.url || item?.product?.path || null

const buildSlideLink = item => {
  const to = getItemLinkTo(item)
  if (!to) return null

  return {
    to,
    children: (
      <>
        {item?.link?.displayText || "View Product Details"}
        <FaAngleRight />
      </>
    ),
    unstyled: true,
    newWindow: item?.link?.newWindow
  }
}

const GalleryGroupedModalBody = ({
  group,
  itemIndex,
  setItemIndex,
  onPrevItem,
  onNextItem,
  disablePrevItem,
  disableNextItem,
  onRequestClose
}) => {
  const items = group?.itemsFiltered || []
  const item = items[itemIndex]
  const contentHtml =
    item?.contentNode?.childMarkdownRemark?.html ||
    (item?.product?.name && `<h3>${item?.product?.name}</h3>`)

  const link = item && buildSlideLink(item)
  const mainImageLinkTo = item && !item?.video ? getItemLinkTo(item) : null
  const mainImageLinkLabel =
    item?.link?.displayText || item?.product?.name || "View product details"

  return (
    <div className={style.galleryNew__modal} data-gallery-new-modal>
      <button
        type="button"
        className={style.galleryNew__modalNavPrev}
        onClick={onPrevItem}
        disabled={disablePrevItem}
        aria-label="Previous gallery item"
      >
        <MdKeyboardArrowLeft />
      </button>

      <div className={style.galleryNew__modalColumn}>
        <div className={style.galleryNew__modalInner}>
          <button
            type="button"
            className={style.galleryNew__modalClose}
            onClick={onRequestClose}
            aria-label="Close gallery"
          >
            <FaTimes />
          </button>

          <div className={style.galleryNew__modalHeader}>
            {group.tagLabel && (
              <p className={style.galleryNew__modalTag}>{group.tagLabel}</p>
            )}
            <h2 className={style.galleryNew__modalTitle}>{group.title}</h2>
            {group.leadText && (
              <p className={style.galleryNew__modalLead}>{group.leadText}</p>
            )}
          </div>

          <div
            className={[
              style.galleryNew__modalMain,
              mainImageLinkTo ? style["galleryNew__modalMain--linked"] : ""
            ]
              .filter(Boolean)
              .join(" ")}
          >
            {mainImageLinkTo ? (
              <Button
                unstyled
                isCardWrap
                to={mainImageLinkTo}
                isNewTab={item?.link?.newWindow}
                title={mainImageLinkLabel}
              >
                <GatsbyImage
                  image={item.image.gatsbyImageData}
                  alt={item.image.alt || ""}
                  loading="eager"
                />
              </Button>
            ) : (
              <>
                {item?.image?.gatsbyImageData && !item?.video && (
                  <GatsbyImage
                    image={item.image.gatsbyImageData}
                    alt={item.image.alt || ""}
                    loading="eager"
                  />
                )}
                {item?.video && (
                  <Video
                    moduleStyle="Default"
                    video={item.video}
                    image={item.image}
                    elevated={false}
                  />
                )}
              </>
            )}
          </div>

          <div className={style.galleryNew__modalFooter}>
            {contentHtml && (
              <div
                className={style.galleryNew__modalItemBody}
                dangerouslySetInnerHTML={{ __html: contentHtml }}
              />
            )}
            {link && (
              <div className={style.galleryNew__modalCta}>
                <Button {...link} isNewTab={link.newWindow} />
              </div>
            )}
          </div>

          {items.length > 1 && (
            <div className={style.galleryNew__modalThumbs} role="tablist">
              {items.map((it, idx) => (
                <button
                  key={it.id || `${group.id}-${idx}`}
                  type="button"
                  role="tab"
                  aria-selected={idx === itemIndex}
                  className={[
                    style.galleryNew__thumb,
                    idx === itemIndex ? style["galleryNew__thumb--active"] : ""
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  onClick={() => setItemIndex(idx)}
                >
                  {it?.image?.gatsbyImageData && (
                    <GatsbyImage
                      image={it.image.gatsbyImageData}
                      alt={it.image.alt || ""}
                      loading="lazy"
                    />
                  )}
                  {it?.video && (
                    <span className={style.galleryNew__thumbPlay} />
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <button
        type="button"
        className={style.galleryNew__modalNavNext}
        onClick={onNextItem}
        disabled={disableNextItem}
        aria-label="Next gallery item"
      >
        <MdKeyboardArrowRight />
      </button>
    </div>
  )
}

const GalleryNew = ({ groups = [], showFilters, location }) => {
  const [isModalOpen, setModalOpen] = useState(false)
  const [modalGroupId, setModalGroupId] = useState(null)
  const [modalItemIndex, setModalItemIndex] = useState(0)
  /** `null` until FilteredList mounts; then the current filtered subset (may be empty array). */
  const [filteredGroupCards, setFilteredGroupCards] = useState(null)

  const allGroupCards = useMemo(() => {
    const cards = []

    ;(groups || []).forEach(group => {
      const itemsFiltered = filterByLocale(group.items || [])
      if (itemsFiltered.length === 0) return

      const galleryProducts =
        itemsFiltered.map(i => i?.product).filter(Boolean) || []

      const getTags = desiredTag => {
        if (!galleryProducts.length) return []
        return galleryProducts
          .map(product => {
            if (!product) return null
            if (desiredTag === "category") {
              return (
                product.category && {
                  name: product.category.title,
                  slug: product.category.slug
                }
              )
            }
            if (desiredTag === "productModel" && product.productModel) {
              return {
                ...transformTagToObject(product.productModel),
                brand: product.brand
              }
            }
            if (desiredTag === "brand") {
              return transformTagToObject(product.brand)
            }
            return null
          })
          .filter(Boolean)
      }

      const galleryBrands = configureTags([...getTags("brand")])
      const galleryModels = configureTags([...getTags("productModel")])
      const galleryCategories = configureTags([...getTags("category")])

      const tagObj = group.tag
        ? {
            name: group.tag.title || group.tag.name,
            slug:
              group.tag.slug || slugify(group.tag.title || group.tag.name || "")
          }
        : null

      const tagLabel =
        typeof tagObj?.name === "string" ? tagObj.name.toUpperCase() : ""

      const stripHtmlFirstItem = html => {
        if (!html || typeof html !== "string") return ""
        const plain = html.replace(/<[^>]+>/g, "").trim()
        return plain.length > 200 ? `${plain.slice(0, 200)}…` : plain
      }

      let excerptText = ""
      if (group.excerpt && String(group.excerpt).trim()) {
        excerptText = String(group.excerpt).trim()
      } else {
        excerptText = stripHtmlFirstItem(
          itemsFiltered[0]?.contentNode?.childMarkdownRemark?.html
        )
      }

      cards.push({
        id: group.id || `gallery-group-${group.originalId || ""}`,
        previewItem: itemsFiltered[0],
        tagLabel,
        tag: tagObj,
        title: group.title,
        leadText: group.excerpt || "",
        itemsFiltered,
        hasVideo: itemsFiltered.some(i => i?.video),
        brand: galleryBrands,
        category: galleryCategories,
        model: galleryModels,
        excerptText,
        elevated: true,
        hover: true
      })
    })

    return cards.map(card => ({
      ...card,
      onClick: (gid, filteredItemsFromList) => {
        const navList =
          filteredItemsFromList && filteredItemsFromList.length > 0
            ? filteredItemsFromList
            : cards
        setFilteredGroupCards(navList)
        setModalGroupId(gid)
        setModalItemIndex(0)
        setModalOpen(true)
      }
    }))
  }, [groups])

  const navigableGroups = useMemo(
    () => (filteredGroupCards === null ? allGroupCards : filteredGroupCards),
    [filteredGroupCards, allGroupCards]
  )

  const modalGroupPayload = useMemo(() => {
    if (!modalGroupId) return null
    const card = navigableGroups.find(g => g.id === modalGroupId)
    if (!card) return null
    return {
      id: card.id,
      title: card.title,
      leadText: card.leadText,
      tagLabel: card.tagLabel,
      itemsFiltered: card.itemsFiltered
    }
  }, [modalGroupId, navigableGroups])

  const modalGroupIndex = navigableGroups.findIndex(g => g.id === modalGroupId)

  useEffect(() => {
    if (isModalOpen && modalGroupId && modalGroupIndex < 0) {
      setModalOpen(false)
      setModalGroupId(null)
    }
  }, [isModalOpen, modalGroupId, modalGroupIndex])

  const galleryBrandsFlat = configureTags(
    allGroupCards.flatMap(c => c.brand || [])
  )
  const galleryCategoriesFlat = configureTags(
    allGroupCards.flatMap(c => c.category || [])
  )
  const galleryModelsFlat = configureTags(
    allGroupCards.flatMap(c => c.model || [])
  )
  const galleryTagsFlat = configureTags(
    allGroupCards.map(c => c.tag).filter(Boolean)
  )

  const filters = []
  galleryBrandsFlat.length > 0 &&
    filters.push({
      name: "brand",
      label: "brand",
      placeholder: {
        text: "Select brand"
      },
      options: [...configTagsForSelect(galleryBrandsFlat)]
    })

  galleryCategoriesFlat.length > 0 &&
    filters.push({
      name: "category",
      label: "category",
      placeholder: {
        text: "Select category"
      },
      options: [...configTagsForSelect(galleryCategoriesFlat)]
    })

  galleryModelsFlat.length > 0 &&
    filters.push({
      name: "model",
      label: "model",
      placeholder: {
        text: "Select model"
      },
      options: [
        ...configTagsForSelect(galleryModelsFlat, [...galleryBrandsFlat])
      ]
    })

  galleryTagsFlat.length > 0 &&
    filters.push({
      name: "tag",
      label: "Setup Type",
      placeholder: {
        text: "Select Setup Type"
      },
      options: [...configTagsForSelect(galleryTagsFlat)]
    })

  const setModal = open => {
    setModalOpen(open)
    if (!open) {
      setModalGroupId(null)
      setModalItemIndex(0)
    }
  }

  const modalItemCount = modalGroupPayload?.itemsFiltered?.length ?? 0

  const shiftItem = delta => {
    if (modalItemCount <= 1) return
    setModalItemIndex(index => {
      const next = index + delta
      return Math.max(0, Math.min(modalItemCount - 1, next))
    })
  }

  return (
    <div className={style.galleryNew}>
      <FilteredList
        items={allGroupCards}
        filters={filters}
        type="gallery_new"
        onChange={filtered => setFilteredGroupCards(filtered)}
        defaultQueries={new Map()}
        showFilters={showFilters}
        location={location}
      />
      <Modal
        onChange={setModal}
        globalState={isModalOpen && !!modalGroupPayload}
        background={"none"}
        overflowScroll
        flexInner
        noPadding
      >
        {modalGroupPayload && (
          <GalleryGroupedModalBody
            group={modalGroupPayload}
            itemIndex={modalItemIndex}
            setItemIndex={setModalItemIndex}
            onPrevItem={() => shiftItem(-1)}
            onNextItem={() => shiftItem(1)}
            disablePrevItem={modalItemIndex <= 0 || modalItemCount <= 1}
            disableNextItem={
              modalItemIndex >= modalItemCount - 1 || modalItemCount <= 1
            }
            onRequestClose={() => setModal(false)}
          />
        )}
      </Modal>
    </div>
  )
}

export default GalleryNew

export const query = graphql`
  fragment GalleryNew on DatoCmsGalleryNew {
    id
    padding
    showFilters
    model {
      apiKey
    }
    linkHash
    __typename
    groups {
      id
      originalId
      title
      excerpt
      tag {
        title
        originalId
      }
      items {
        ...GalleryItem
      }
    }
  }
`
